import { type ReactElement, useMemo, useRef } from 'react';
import { hasVisibleStackedChartValue } from '@/features/executionRuns/pages/executionRunsCharts';
import { getErrorCopy } from '@/shared/copy/errors';
import { s, typographyTokens } from '@/theme/tokens';
import { CHART_EMPTY_STATE_NO_DATA_LABEL, ChartEmptyState } from '@/ui/composites';
import { Box, ChartSwatch, Text } from '@/ui/primitives';
import { formatChartTooltipDateLabel } from '@/ui/primitives/chartDateLabel';
import { StackedBarChart } from '@/ui/primitives/StackedBarChart';
import { StackedBarChartSkeleton } from '@/ui/primitives/StackedBarChartSkeleton';
import {
  buildChartPanelCenteredStateSx,
  buildStatusStackedChartHostSx,
  buildStatusStackedLegendLayoutRecipe,
  chartPanelColumnContentSx,
} from './chartPanelSurfaceSx';
import { ChartPanelTitle } from './ChartPanelTitle';
import { MetricsChartPanelFrame } from './MetricsChartPanelFrame';
import { type StackedMetricsStatusChartPanelLayout } from './StackedMetricsStatusChartSection.layout';
import {
  formatStackedStatusChartLabel,
  renderStatusChartLegendLoadingState,
} from './StackedMetricsStatusChartSection.states';
import { useChartPanelMeasuredSize } from './useChartPanelMeasuredSize';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type {
  MetricsStatusChartPanelDisplayModel,
  MetricsStatusChartPanelModelToolbarState,
} from './useMetricsStatusChartPanelModel';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

const skeletonPlotTopAdjustmentPx = 8;
const skeletonPlotBottomAdjustmentPx = 24;
const statusChartAxisLabelFontSize = '11px !important';
const statusChartAxisLabelOpacity = 0.88;
const statusChartLegendFontSizePx = 13;
const statusChartLegendLineHeight = 1.3;
const statusChartLegendSwatchOffsetY = '0.5px';

type StatusStackedChartPanelProps<TStatus extends string> = {
  title: string;
  searchAriaLabel: string;
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  availableTags: MetricsToolbarTagOption[];
  loading: boolean;
  showPlaceholder?: boolean;
  toolbarState: MetricsStatusChartPanelModelToolbarState<TStatus>;
  statusPanel: StackedMetricsStatusChartPanelLayout;
  statusChartVisualHeight: number;
  legendData: StatusPieDatum[];
  legendLoading: boolean;
  statusEmptyTitle: string;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  showStatusChartSkeleton: boolean;
  showChartErrorState: boolean;
  suppressAnimation: boolean;
  showAllXAxisLabels?: boolean;
};

export const StatusStackedChartPanel = function StatusStackedChartPanel<TStatus extends string>({
  title,
  loading,
  showPlaceholder = false,
  statusPanel,
  statusChartVisualHeight,
  legendData,
  legendLoading,
  statusEmptyTitle: _statusEmptyTitle,
  statusChartData,
  showStatusChartSkeleton,
  showChartErrorState,
  suppressAnimation,
  showAllXAxisLabels = true,
}: StatusStackedChartPanelProps<TStatus>): ReactElement {
  const chartHostRef = useRef<HTMLDivElement | null>(null);
  const chartHostSize = useChartPanelMeasuredSize({
    fallbackHeight: statusChartVisualHeight,
    fallbackWidth: statusChartVisualHeight,
    targetRef: chartHostRef,
  });
  const legendLayoutRecipe = useMemo(
    () =>
      buildStatusStackedLegendLayoutRecipe({
        fontSizePx: statusChartLegendFontSizePx,
        lineHeight: statusChartLegendLineHeight,
        swatchOffsetY: statusChartLegendSwatchOffsetY,
      }),
    [],
  );
  const chartHostSx = useMemo(
    () =>
      buildStatusStackedChartHostSx({
        axisLabelFontSize: statusChartAxisLabelFontSize,
        axisLabelFontWeight: typographyTokens.body.fontWeight,
        axisLabelOpacity: statusChartAxisLabelOpacity,
        paddingBottom: statusPanel.chartHostPaddingBottom,
        paddingTop: statusPanel.chartHostPaddingTop,
        paddingX: statusPanel.chartHostPaddingX,
      }),
    [
      statusPanel.chartHostPaddingBottom,
      statusPanel.chartHostPaddingTop,
      statusPanel.chartHostPaddingX,
    ],
  );

  const legendNode = useMemo((): ReactElement | null => {
    if (legendLoading) return renderStatusChartLegendLoadingState();
    if (legendData.length === 0) return null;
    return (
      <Box sx={legendLayoutRecipe.legendSx}>
        {legendData.map((entry, index) => (
          <Box key={`${entry.id}-${String(index)}`} sx={legendLayoutRecipe.itemSx}>
            <Box sx={legendLayoutRecipe.swatchSx}>
              <ChartSwatch color={entry.color} />
            </Box>
            <Text
              role="body"
              color="textSecondary"
              component="span"
              sx={legendLayoutRecipe.labelSx}
            >
              {entry.name}
            </Text>
          </Box>
        ))}
      </Box>
    );
  }, [legendData, legendLayoutRecipe, legendLoading]);

  const header = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        rowGap: s('md'),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <ChartPanelTitle
          title={title}
          component="h2"
          fontSizePx={14}
          fontWeight={500}
          showInfoIcon={false}
        />
      </Box>
      {legendNode ? <Box sx={legendLayoutRecipe.headerSlotSx}>{legendNode}</Box> : null}
    </Box>
  );

  const resolvedChartHeight = Math.max(140, chartHostSize.height);
  const hasVisibleStatusChartData =
    statusChartData !== null &&
    hasVisibleStackedChartValue(statusChartData.data, statusChartData.series);
  const showChartSkeleton = loading || showStatusChartSkeleton;

  return (
    <MetricsChartPanelFrame
      height={statusPanel.height}
      header={header}
      headerPaddingX={s('xl')}
      headerPaddingTop={s('md')}
      headerPaddingBottom={s('xs')}
      contentPaddingBottom={s('sm')}
      headerSx={{
        minHeight: `${statusPanel.headerHeight}px`,
      }}
      contentSx={chartPanelColumnContentSx}
    >
      <Box sx={chartHostSx}>
        <Box ref={chartHostRef} sx={{ flex: '1 1 auto', minHeight: 0 }}>
          {showPlaceholder ? (
            <Box aria-hidden="true" sx={{ height: resolvedChartHeight }} />
          ) : showChartSkeleton ? (
            <StackedBarChartSkeleton
              height={resolvedChartHeight}
              barCornerRadius={statusPanel.barCornerRadius}
              categoryGapRatio={statusPanel.categoryGapRatio}
              marginLeft={statusPanel.chartMargins.left}
              marginRight={statusPanel.chartMargins.right}
              marginTop={statusPanel.headerToChartGap + skeletonPlotTopAdjustmentPx}
              marginBottom={Math.max(
                0,
                statusPanel.chartMargins.bottom + skeletonPlotBottomAdjustmentPx,
              )}
              yAxisWidth={statusPanel.yAxisWidth}
              hideYAxisLine
            />
          ) : showChartErrorState && !hasVisibleStatusChartData ? (
            <Box sx={buildChartPanelCenteredStateSx({ height: resolvedChartHeight })}>
              <ChartEmptyState
                title={getErrorCopy('banner.chartLoadFailed')}
                variant="metricCard"
              />
            </Box>
          ) : statusChartData && hasVisibleStatusChartData ? (
            <StackedBarChart
              data={statusChartData.data}
              series={statusChartData.series}
              height={resolvedChartHeight}
              showAllXAxisLabels={showAllXAxisLabels}
              xAxisLabelFormatter={formatStackedStatusChartLabel}
              tooltipLabelFormatter={formatChartTooltipDateLabel}
              marginLeft={statusPanel.chartMargins.left}
              marginRight={statusPanel.chartMargins.right}
              marginTop={statusPanel.headerToChartGap}
              marginBottom={statusPanel.chartMargins.bottom}
              yAxisWidth={statusPanel.yAxisWidth}
              yAxisAutoWidth={statusPanel.yAxisAutoWidth}
              yAxisAutoWidthPadding={statusPanel.yAxisAutoWidthPadding}
              yAxisHeadroomTicks={1}
              yAxisTopPaddingRatio={0}
              axisStrokeWidth={statusPanel.axisStrokeWidth}
              barCornerRadius={statusPanel.barCornerRadius}
              categoryGapRatio={statusPanel.categoryGapRatio}
              barGapRatio={statusPanel.barGapRatio}
              yAxisLabelOffset={statusPanel.yAxisLabelOffset}
              hideXAxisTicks={statusPanel.hideXAxisTicks}
              xAxisLabelOffset={statusPanel.xAxisLabelOffset}
              showHorizontalGrid
              showPlotAreaFrame
              hideXAxisLine={false}
              hideYAxisLine
              skipAnimation={suppressAnimation}
              tooltipEmptyValueLabel="No runs"
            />
          ) : (
            <Box sx={buildChartPanelCenteredStateSx({ height: resolvedChartHeight })}>
              <ChartEmptyState title={CHART_EMPTY_STATE_NO_DATA_LABEL} variant="metricCard" />
            </Box>
          )}
        </Box>
      </Box>
    </MetricsChartPanelFrame>
  );
};
